import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createVoiceCallController } from "../services/voiceCall";

// Call phases used by the UI.
//   idle         — no call in progress
//   outgoing     — we sent an invite, waiting for accept
//   incoming     — someone is calling us, waiting for user to answer/decline
//   connecting   — accepted, SDP handshake in flight
//   active       — audio flowing
//   reconnecting — transient ICE/transport hiccup; WebRTC is recovering or
//                  an ICE restart re-offer is in flight. The call survives.
//
// The context intentionally stays small — the widgets read `state` and
// dispatch actions via `actions.*`. Signaling and RTCPeerConnection live
// entirely inside the controller.
const VoiceCallContext = createContext(null);

const INITIAL_STATE = {
    phase: "idle",
    callId: null,
    peer: null, // { id, name, avatar_url } for both incoming and outgoing
    // A second call that arrived while we were busy. It keeps ringing
    // (banner + ringtone) instead of being auto-rejected; accepting it
    // switches over and ends the call in progress.
    pendingIncoming: null, // { callId, peer } | null
    isMuted: false,
    startedAt: null,
    connectionState: null,
    error: null,
};

// Offline-callee retry cap + interval. Conservative to avoid backend
// rate-limiting repeated call_invite to an offline user; the proper long-term
// fix is a backend presence push so the client invites exactly once.
const MAX_RETRIES = 6;
const RETRY_INTERVAL = 8000;

export function VoiceCallProvider({ children }) {
    const [state, setState] = useState(INITIAL_STATE);
    const controllerRef = useRef(null);
    const remoteAudioRef = useRef(null);
    // Keep a ref-copy of state so the socket handlers (which close over the
    // controller from a stale render) always see fresh values.
    const stateRef = useRef(state);
    stateRef.current = state;

    // ICE-restart bookkeeping for resilience. Both are cleared in reset().
    //   restartAttemptedRef — at most one restart attempt per call (prevents
    //     a failed→restart→failed loop from spinning forever).
    //   reconnectFallbackRef — answerer-side 20s timer: if the offerer's
    //     re-offer doesn't bring the connection back in time, give up.
    const restartAttemptedRef = useRef(false);
    const reconnectFallbackRef = useRef(null);

    // Offline-callee retry. When the backend acks an invite with
    // callee_online=false, keep ringing ("Шырылдап жатыр") and re-invite on a
    // timer until the callee comes online or we hit MAX_RETRIES. Cleared in
    // reset() / at the start of invite().
    const peerOfflineRef = useRef(false);
    const retryTimerRef = useRef(null);
    const retryCountRef = useRef(0);

    const attachRemoteStream = useCallback((stream) => {
        const el = remoteAudioRef.current;
        if (!el) {
            console.warn("[voice_call] attachRemoteStream: no <audio> element (ref null)");
            return;
        }
        el.srcObject = stream;
        console.log("[voice_call] attached remote stream, audio tracks:", stream?.getAudioTracks?.().length ?? 0, "muted:", el.muted, "paused:", el.paused);
        // Some browsers require an explicit play() after the src is swapped.
        // A rejection (e.g. autoplay blocked) means the peer's audio is
        // attached but inaudible — log it so one-way audio is diagnosable.
        el.play().catch((err) => console.warn("[voice_call] remote audio play() rejected:", err?.name));
    }, []);

    const detachRemoteStream = useCallback(() => {
        if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = null;
        }
    }, []);

    const clearRetry = useCallback(() => {
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
        retryCountRef.current = 0;
        peerOfflineRef.current = false;
    }, []);

    const reset = useCallback(
        (nextError) => {
            console.warn("[voice_call] reset()", nextError, new Error().stack);
            if (reconnectFallbackRef.current) {
                clearTimeout(reconnectFallbackRef.current);
                reconnectFallbackRef.current = null;
            }
            restartAttemptedRef.current = false;
            clearRetry();
            detachRemoteStream();
            // A call queued while we were busy becomes the ringing call now —
            // it never got rejected, so it should keep coming.
            setState((prev) => {
                const pending = prev.pendingIncoming;
                if (pending) {
                    return {
                        ...INITIAL_STATE,
                        error: nextError ?? null,
                        phase: "incoming",
                        callId: pending.callId,
                        peer: pending.peer,
                    };
                }
                return { ...INITIAL_STATE, error: nextError ?? null };
            });
        },
        [detachRemoteStream, clearRetry],
    );

    // Offline callee: keep ringing and re-invite on a timer until the callee
    // comes online or we hit MAX_RETRIES. Ack-driven — each retry's ack re-arms
    // the next, so invites never overlap. Returns synchronously.
    const scheduleRetry = useCallback(() => {
        if (retryCountRef.current >= MAX_RETRIES) {
            controllerRef.current?.teardown();
            reset("peerOffline");
            return;
        }
        retryCountRef.current += 1;
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            if (stateRef.current.phase !== "outgoing") return;
            if (!peerOfflineRef.current) return;
            const peerId = stateRef.current.peer?.id;
            if (!peerId) return;
            controllerRef.current?.invite(peerId);
        }, RETRY_INTERVAL);
    }, [reset]);

    useEffect(() => {
        const handlers = {
            onIncoming: ({ call_id, from_user_id, from_user_name, from_user_avatar }) => {
                const peer = { id: from_user_id, name: from_user_name, avatar_url: from_user_avatar };
                if (stateRef.current.phase === "idle") {
                    setState({
                        ...INITIAL_STATE,
                        phase: "incoming",
                        callId: call_id,
                        peer,
                    });
                    return;
                }
                // Busy (talking / ringing / connecting / calling out): don't
                // reject — queue the call so it keeps ringing. The user either
                // finishes the current call (reset promotes it) or switches.
                setState((prev) => ({ ...prev, pendingIncoming: { callId: call_id, peer } }));
            },
            onInviteAck: ({ call_id, callee_online }) => {
                // Backend allocates the call_id after we send the invite; this
                // ack is our first chance to learn it. Without saving it here,
                // every later handler (accepted / peer_offer / etc.) rejects
                // itself because stateRef.current.callId is still null.
                if (stateRef.current.phase !== "outgoing") return;
                // Sync update so a same-tick onAccepted reads the fresh callId
                // (setState is async; without this, onAccepted's callId guard
                // races and drops a real accept).
                stateRef.current = { ...stateRef.current, callId: call_id };
                setState((prev) =>
                    prev.phase === "outgoing" ? { ...prev, callId: call_id } : prev,
                );
                if (callee_online) {
                    // Callee is online — stop retrying and wait for an answer.
                    clearRetry();
                } else {
                    // Callee offline — keep ringing and re-invite shortly.
                    peerOfflineRef.current = true;
                    scheduleRetry();
                }
            },
            onAccepted: async ({ call_id }) => {
                // Ignore unless we're the caller still waiting for an answer.
                if (stateRef.current.phase !== "outgoing") return;
                // The invite_ack can land after the accepted if the callee
                // answers instantly — adopt the call_id then, otherwise we'd
                // drop the accept and stay stuck ringing (ringback forever,
                // no audio).
                if (stateRef.current.callId && stateRef.current.callId !== call_id) return;
                if (!stateRef.current.callId) {
                    stateRef.current = { ...stateRef.current, callId: call_id };
                    setState((prev) => (prev.phase === "outgoing" ? { ...prev, callId: call_id } : prev));
                }
                clearRetry();
                setState((prev) => ({ ...prev, phase: "connecting" }));
                try {
                    await controllerRef.current.createAndSendOffer(call_id);
                } catch (err) {
                    console.error("[voice_call] createAndSendOffer failed", err);
                    controllerRef.current?.hangup(call_id);
                    reset("offer_failed");
                }
            },
            onRejected: ({ call_id, reason }) => {
                // Offline callee: keep ringing (the retry timer drives
                // re-invites). Must be checked BEFORE the callId guard below —
                // a rejection for the current call_id would pass that guard and
                // tear down a call we want to keep alive. Also covers the
                // null-callId hole (rejection arriving before the ack): treat
                // as offline and ignore.
                const offlineReason = [
                    "offline", "peerOffline", "user_offline",
                    "unreachable", "unavailable", "no_internet",
                ].includes(reason);
                if (
                    peerOfflineRef.current ||
                    offlineReason ||
                    (stateRef.current.callId == null && stateRef.current.phase === "outgoing")
                ) {
                    return;
                }
                if (stateRef.current.callId && stateRef.current.callId !== call_id) return;
                controllerRef.current?.teardown();
                reset(reason || "declined");
            },
            onHungup: ({ call_id }) => {
                if (stateRef.current.callId !== call_id) return;
                controllerRef.current?.teardown();
                reset(null);
            },
            onPeerOffer: async ({ call_id, sdp }) => {
                try {
                    await controllerRef.current.handlePeerOffer(call_id, sdp);
                } catch (err) {
                    console.error("[voice_call] handlePeerOffer failed", err);
                    controllerRef.current?.hangup(call_id);
                    reset("offer_failed");
                }
            },
            onPeerAnswer: async ({ sdp }) => {
                try {
                    await controllerRef.current.handlePeerAnswer(sdp);
                } catch (err) {
                    console.warn("[voice_call] handlePeerAnswer failed", err);
                }
            },
            onPeerIce: async ({ candidate }) => {
                await controllerRef.current.handlePeerIce(candidate);
            },
            onError: ({ call_id, error }) => {
                console.warn("[voice_call] server error", error);
                // The queued call expired or was cancelled server-side — stop
                // showing it, but keep the call in progress untouched.
                if (call_id && stateRef.current.pendingIncoming?.callId === call_id) {
                    setState((prev) =>
                        prev.pendingIncoming?.callId === call_id ? { ...prev, pendingIncoming: null } : prev,
                    );
                    return;
                }
                // If the backend errors while we're retrying toward an offline
                // callee (e.g. a rate-limit), stop retrying and surface offline.
                if (peerOfflineRef.current) {
                    clearRetry();
                    controllerRef.current?.teardown();
                    reset("peerOffline");
                }
            },
            onRemoteStream: (stream) => {
                const tracks = stream?.getAudioTracks?.() ?? [];
                console.log("[voice_call] remote stream received, audio tracks:", tracks.length, tracks.map((t) => ({ enabled: t.enabled, muted: t.muted, ready: t.readyState })));
                attachRemoteStream(stream);
                setState((prev) => ({ ...prev, phase: "active", startedAt: Date.now() }));
            },
            onConnectionStateChange: (connectionState) => {
                console.warn("[voice_call] connectionState:", connectionState);
                setState((prev) => ({ ...prev, connectionState }));

                if (connectionState === "connected" || connectionState === "completed") {
                    // (Re)connected — cancel any pending fallback and come
                    // back out of the reconnecting status.
                    if (reconnectFallbackRef.current) {
                        clearTimeout(reconnectFallbackRef.current);
                        reconnectFallbackRef.current = null;
                    }
                    setState((prev) =>
                        prev.phase === "reconnecting" ? { ...prev, phase: "active" } : prev,
                    );
                    return;
                }

                if (connectionState === "disconnected") {
                    // Transient transport drop — WebRTC usually self-recovers.
                    // Keep the call alive but surface a reconnecting status.
                    setState((prev) =>
                        prev.phase === "active" ? { ...prev, phase: "reconnecting" } : prev,
                    );
                    return;
                }

                if (connectionState === "failed") {
                    // One ICE restart per call. The offerer re-offers; the
                    // answerer can't drive a restart, so it waits (with a
                    // 20s fallback) for the offerer's re-offer to land here
                    // via call:peer_offer → handlePeerOffer (reuses the pc).
                    if (restartAttemptedRef.current) {
                        controllerRef.current?.teardown();
                        reset("connection_failed");
                        return;
                    }
                    restartAttemptedRef.current = true;
                    setState((prev) => ({ ...prev, phase: "reconnecting" }));
                    const { callId } = stateRef.current;
                    const restarted = controllerRef.current?.restartIce(callId);
                    // restartIce is async (returns a promise); treat a falsy
                    // immediate value (no pc / answerer) as "wait for re-offer".
                    if (!restarted) {
                        if (reconnectFallbackRef.current) clearTimeout(reconnectFallbackRef.current);
                        reconnectFallbackRef.current = setTimeout(() => {
                            reconnectFallbackRef.current = null;
                            controllerRef.current?.teardown();
                            reset("connection_failed");
                        }, 20000);
                    }
                    return;
                }

                if (connectionState === "closed") {
                    controllerRef.current?.teardown();
                    reset(null);
                }
            },
        };

        controllerRef.current = createVoiceCallController(handlers);

        // Surface "Жалғануда" the instant connectivity drops — faster than
        // waiting for WebRTC's slow `disconnected` state. onConnectionStateChange
        // remains the authoritative resolver; these just flip the phase early.
        const goReconnecting = () =>
            setState((prev) => (prev.phase === "active" ? { ...prev, phase: "reconnecting" } : prev));
        const maybeRecover = () =>
            setState((prev) => {
                if (prev.phase !== "reconnecting") return prev;
                if (prev.connectionState === "connected" || prev.connectionState === "completed") {
                    return { ...prev, phase: "active" };
                }
                return prev;
            });
        const onReconnectFailed = () => {
            controllerRef.current?.teardown();
            reset("connection_failed");
        };
        window.addEventListener("offline", goReconnecting);
        window.addEventListener("online", maybeRecover);
        window.addEventListener("socket:disconnected", goReconnecting);
        window.addEventListener("socket:reconnected", maybeRecover);
        window.addEventListener("socket:reconnect_failed", onReconnectFailed);

        return () => {
            window.removeEventListener("offline", goReconnecting);
            window.removeEventListener("online", maybeRecover);
            window.removeEventListener("socket:disconnected", goReconnecting);
            window.removeEventListener("socket:reconnected", maybeRecover);
            window.removeEventListener("socket:reconnect_failed", onReconnectFailed);
            controllerRef.current?.destroy();
        };
    }, [attachRemoteStream, reset, clearRetry, scheduleRetry]);

    // Sync mute state to the controller. Keeping the side effect out of the
    // toggleMute updater (React double-invokes updaters in StrictMode, which
    // can drop a side effect placed inside one) makes muting reliable: state
    // is the source of truth, this effect applies it once per change.
    useEffect(() => {
        if (state.phase !== "active") return;
        controllerRef.current?.setMuted(state.isMuted);
    }, [state.isMuted, state.phase]);

    const invite = useCallback(async (targetUser) => {
        if (!targetUser?.id) return;
        clearRetry();
        setState({
            ...INITIAL_STATE,
            phase: "outgoing",
            peer: { id: targetUser.id, name: targetUser.name, avatar_url: targetUser.avatar_url },
        });
        try {
            await controllerRef.current.invite(targetUser.id);
        } catch (err) {
            console.error("[voice_call] invite failed", err);
            reset("mic_permission_denied");
        }
    }, [reset, clearRetry]);

    const acceptIncoming = useCallback(async () => {
        const { callId } = stateRef.current;
        if (!callId) return;
        setState((prev) => ({ ...prev, phase: "connecting" }));
        controllerRef.current.accept(callId);
    }, []);

    const rejectIncoming = useCallback(() => {
        const { callId } = stateRef.current;
        if (callId) controllerRef.current.reject(callId, "declined");
        controllerRef.current?.teardown();
        reset(null);
    }, [reset]);

    // Accept the call that arrived while we were busy: end the current call
    // (the peer gets call_hangup), release media, then switch over to the
    // new call and accept it.
    const acceptPendingIncoming = useCallback(() => {
        const pending = stateRef.current.pendingIncoming;
        if (!pending) return;
        const prev = stateRef.current;
        if (prev.callId) {
            // Not yet connected — decline it; otherwise hang up the live call.
            if (prev.phase === "incoming") controllerRef.current.reject(prev.callId, "declined");
            else controllerRef.current.hangup(prev.callId);
        }
        controllerRef.current?.teardown();
        if (reconnectFallbackRef.current) {
            clearTimeout(reconnectFallbackRef.current);
            reconnectFallbackRef.current = null;
        }
        restartAttemptedRef.current = false;
        clearRetry();
        detachRemoteStream();
        // Sync ref update so a fast call:hungup echo for the old call_id can't
        // match the stale stateRef and tear the fresh state down again.
        const next = { ...INITIAL_STATE, phase: "connecting", callId: pending.callId, peer: pending.peer };
        stateRef.current = next;
        setState(next);
        controllerRef.current.accept(pending.callId);
    }, [clearRetry, detachRemoteStream]);

    const rejectPendingIncoming = useCallback(() => {
        const pending = stateRef.current.pendingIncoming;
        if (!pending) return;
        controllerRef.current.reject(pending.callId, "declined");
        stateRef.current = { ...stateRef.current, pendingIncoming: null };
        setState((prev) => ({ ...prev, pendingIncoming: null }));
    }, []);

    const hangup = useCallback(() => {
        const { callId } = stateRef.current;
        if (callId) controllerRef.current.hangup(callId);
        else controllerRef.current?.teardown();
        reset(null);
    }, [reset]);

    const toggleMute = useCallback(() => {
        setState((prev) => ({ ...prev, isMuted: !prev.isMuted }));
    }, []);

    const clearError = useCallback(() => {
        setState((prev) => (prev.error ? { ...prev, error: null } : prev));
    }, []);

    const value = useMemo(
        () => ({
            state,
            actions: {
                invite,
                acceptIncoming,
                rejectIncoming,
                acceptPendingIncoming,
                rejectPendingIncoming,
                hangup,
                toggleMute,
                clearError,
            },
        }),
        [state, invite, acceptIncoming, rejectIncoming, acceptPendingIncoming, rejectPendingIncoming, hangup, toggleMute, clearError],
    );

    return (
        <VoiceCallContext.Provider value={value}>
            {children}
            {/* Remote audio lives in the DOM at the provider root so playback
                survives call-screen re-renders (e.g. minimize to the floating
                bubble). A detached `new Audio()` can be paused by the browser
                when the heavy call overlay unmounts. */}
            <audio ref={remoteAudioRef} autoPlay />
        </VoiceCallContext.Provider>
    );
}

export function useVoiceCall() {
    const ctx = useContext(VoiceCallContext);
    if (!ctx) throw new Error("useVoiceCall must be used inside VoiceCallProvider");
    return ctx;
}
